import {ErrorCode} from '@sollapay/enums';
import {AVATAR_ALLOWED_MIME_TYPES, AVATAR_MAX_BYTES} from '@sollapay/utils';
import {usePendingImageUpload} from '@sollapay/web-core';
import {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {showResultToastCard} from '@/components';
import {useApiClient} from '@/hooks';
import {getErrorCode, getTranslatedErrorMessage} from '@/utils';

import {accountSettingsApi} from '../api';
import {useUpdateUserProfileMutation} from './useUpdateUserProfileMutation';

import type {AccountSettingsMode} from '../types';
import type {UserProfileDTO} from '@sollapay/types';

export interface AccountSettingsFormValues {
  firstName: string;
  lastName: string;
  firstNameEn: string;
  lastNameEn: string;
}

const EMPTY_ACCOUNT_SETTINGS_FORM_VALUES: AccountSettingsFormValues = {
  firstName: '',
  lastName: '',
  firstNameEn: '',
  lastNameEn: ''
};

function mapProfileToFormValues(profile?: UserProfileDTO): AccountSettingsFormValues {
  return {
    firstName: profile?.firstName ?? '',
    lastName: profile?.lastName ?? '',
    firstNameEn: profile?.firstNameEn ?? '',
    lastNameEn: profile?.lastNameEn ?? ''
  };
}

export interface UseAccountSettingsFormParams {
  isOpen: boolean;
  profile?: UserProfileDTO;
  mode: AccountSettingsMode;
  onSuccess?: () => void;
}

export function useAccountSettingsForm({
  isOpen,
  profile,
  mode,
  onSuccess
}: UseAccountSettingsFormParams) {
  const {t} = useTranslation(['settings', 'common']);
  const client = useApiClient();
  const updateProfileMutation = useUpdateUserProfileMutation();

  const form = useForm<AccountSettingsFormValues>({
    defaultValues: EMPTY_ACCOUNT_SETTINGS_FORM_VALUES,
    // Welcome stays error-free until the first submit, then re-validates as the user types.
    mode: mode === 'welcome' ? 'onSubmit' : 'onTouched',
    reValidateMode: 'onChange'
  });

  const avatarUpload = usePendingImageUpload({
    maxBytes: AVATAR_MAX_BYTES,
    allowedMimeTypes: AVATAR_ALLOWED_MIME_TYPES,
    currentUrl: profile?.avatarUrl ?? undefined,
    tooLargeMessage: t(`common:errors.${ErrorCode.AVATAR_TOO_LARGE}`),
    unsupportedTypeMessage: t(`common:errors.${ErrorCode.UNSUPPORTED_AVATAR_TYPE}`)
  });

  // usePendingImageUpload owns the file/preview/validation state only — upload-in-flight stays
  // here, since it must cover the S3 PUT phase too, which finishes before the profile mutation
  // (whose own isPending starts) is ever called.
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  // The modal stays mounted across open/close (Modal owns the close animation via `isOpen`),
  // so reset on every open rather than relying on defaultValues from a stale prior submission.
  // Welcome mode starts blank (the user types their name instead of accepting the IdP's), so a
  // profile refetch mid-typing must not re-run the reset.
  const resetSource = mode === 'welcome' ? undefined : profile;
  useEffect(() => {
    if (isOpen) {
      form.reset(
        resetSource ? mapProfileToFormValues(resetSource) : EMPTY_ACCOUNT_SETTINGS_FORM_VALUES
      );
      avatarUpload.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, resetSource, form]);

  const handleSave = form.handleSubmit(async values => {
    try {
      let avatarKey: string | null | undefined;

      if (avatarUpload.pendingFile) {
        setIsUploadingAvatar(true);
        const {data: uploadUrlDto} = await accountSettingsApi.createAvatarUploadUrl(client, {
          fileName: avatarUpload.pendingFile.name,
          contentType: avatarUpload.pendingFile.type,
          fileSize: avatarUpload.pendingFile.size
        });
        await accountSettingsApi.uploadAvatarToS3(uploadUrlDto.uploadUrl, avatarUpload.pendingFile);
        avatarKey = uploadUrlDto.key;
      } else if (avatarUpload.isRemoved) {
        avatarKey = null;
      }

      await updateProfileMutation.mutateAsync({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        firstNameEn: values.firstNameEn.trim(),
        lastNameEn: values.lastNameEn.trim(),
        ...(avatarKey !== undefined ? {avatarKey} : {})
      });

      form.reset(values);
      avatarUpload.reset();
      showResultToastCard({
        isError: false,
        title: t('accountModal.resultToast.success.title'),
        description: t('accountModal.resultToast.success.description'),
        closeLabel: t('accountModal.resultToast.close')
      });
      onSuccess?.();
    } catch (error) {
      const code = getErrorCode(error);
      if (code === ErrorCode.AVATAR_TOO_LARGE || code === ErrorCode.UNSUPPORTED_AVATAR_TYPE) {
        avatarUpload.setError(getTranslatedErrorMessage(error, t));
      } else {
        showResultToastCard({
          isError: true,
          title: t('accountModal.resultToast.error.title'),
          description: getTranslatedErrorMessage(error, t),
          closeLabel: t('accountModal.resultToast.close')
        });
      }
    } finally {
      setIsUploadingAvatar(false);
    }
  });

  return {
    form,
    handleSave,
    isSaving: isUploadingAvatar || updateProfileMutation.isPending,
    avatarUrl: avatarUpload.previewUrl,
    avatarError: avatarUpload.error,
    hasAvatarChange: avatarUpload.hasChange,
    onAvatarFileSelect: avatarUpload.onFileSelect,
    onRemoveAvatar: avatarUpload.onRemove
  };
}
